export class FormValidator {
	#root = null;
	
	constructor (root){
		this.#root = root;
	}
	
	#findFields (){
		const result = [];
		const traverse = (node) => {
		    // Guard against undefined/null nodes
		    if (!node) return;
		    
		    if (
		    	(node.tagName === 'INPUT' && (node.type === 'text' || node.type === 'number' || node.type === 'date')) ||
		        node.tagName === 'TEXTAREA' ||
		        node.tagName === 'SELECT'
		    )
				result.push(node);
		    
		    [{"root" : node, "query" : ":scope > *"}, {"root" : node.shadowRoot, "query" : ":host > *"}].forEach (item => {
				if (item.root)
					item.root.querySelectorAll (item.query).forEach (child => traverse (child));
		    });
		};

		traverse (this.#root);
		return result;
	}	

	#isVisible (node){
	    while (node){
	        if (node.nodeType === Node.ELEMENT_NODE){
	            const style = window.getComputedStyle(node);
	            if (style.display === "none" || style.visibility === "hidden" || node.visible === false)
	                return false;
	        } else if (node.visible === false)
	        	return false;
	        
	        if (node.parentNode)
	            node = node.parentNode;
	        else if (node instanceof ShadowRoot && node.host)
	            node = node.host;
	        else
	            break;
	    }
	    return true;
	}

	#getInvalidRequiredFields (nodes){
	    return nodes.filter(node => {
			const isRequired = (node) => node.required === true || node.getAttribute ("required") !== null;
			
			// Check disabled
			const disabled = node.disabled === true || node.getAttribute ("disabled") !== null;
			if (disabled)
				return false;

			// Check visibility
	        if (!this.#isVisible(node))
	        	return false;

			// Check value and pattern based on type
			if (node.tagName === "INPUT") {
				const type = node.type;
				const val = node.value || "";
				
				// Check required attribute
				if (isRequired (node) && !val)
					return true;

				// For text inputs, if a pattern attribute exists, validate it
				if (type === "text" && node.hasAttribute("pattern") && node.getAttribute("pattern")) {
					const pattern = node.getAttribute("pattern");
					try {
						const re = new RegExp("^(?:" + pattern + ")$");
						if (!re.test(val))
							return true;
					} catch (e) {
						// Invalid regex in pattern - ignore pattern validation
					}
				}

				// For other input types (number, date) just check non-empty (already done)
				return false;
			}

			if (node.tagName === "TEXTAREA")
				return isRequired (node) && !node.value;

	        if (node.tagName === "SELECT")
	            return isRequired (node) && (!node.value || node.value === "0");

	        return false;
	    });
	}

	#setNodeValidity (node, valid){
	    if (valid){
	        node.classList.add("is-valid");
	        node.classList.remove("is-invalid");
	        node.setAttribute("aria-invalid", "false");
	        node.style.borderColor = "";
	    } else {
	        node.classList.add("is-invalid");
	        node.classList.remove("is-valid");
	        node.setAttribute("aria-invalid", "true");
	        node.style.borderColor = "red";
	    }
	}
	
	#findComponents() {
		const result = [];
		const RootClass = (typeof window !== 'undefined') ? window.ShadowDOMElement : null;

		const traverse = (node) => {
			if (!node) return;

			// If node is a ShadowDOMElement subclass
			if (RootClass && node instanceof RootClass) {
				try {
					if (typeof node.isTransient === 'function' && node.isTransient())
						return; // skip transient components
				} catch (e) {
					// ignore errors from isTransient
				}
				
				// Only add if component has a validate method
				if (typeof node.validate === 'function') {
					if (!result.includes(node))
						result.push(node);
				}
				
				// Always descend into shadowRoot to find nested components with validate()
				if (node.shadowRoot) {
					node.shadowRoot.querySelectorAll(":host > *").forEach(child => traverse(child));
				}
				return;
			}

			// Otherwise descend into children and shadowRoot as in #findFields()
			[{ "root": node, "query": ":scope > *" }, { "root": node.shadowRoot, "query": ":host > *" }].forEach(item => {
				if (item.root)
					item.root.querySelectorAll(item.query).forEach(child => traverse(child));
			});
		};

		traverse(this.#root);
		return result;
	}
	
	#validateComponents () {
		const components = this.#findComponents ();
		let validity = true;

		components.forEach (comp => {
			if (comp && typeof comp.validate === 'function') {
				try {
					const result = comp.validate ();
					if (result === false)
						validity = false;
				} catch (e) {
					// If validate throws, consider the component invalid
					validity = false;
				}
			}
		});

		return validity;
	}

	validate (){
	    const fields = this.#findFields ();
	    const invalidFields = this.#getInvalidRequiredFields(fields);

	    fields.forEach(field => {
	        const isValid = !invalidFields.includes(field);
	        this.#setNodeValidity (field, isValid);
	    });

		let componentsValidity = this.#validateComponents ();
		return invalidFields.length === 0 && componentsValidity;
	}
}

if (typeof window !== 'undefined')
	window.FormValidator = FormValidator;
